import { useContext, useState } from 'react';
import styles from './SelectPlatform.module.css';
import PlatformIcon from '../PlatformIcon';
import { Checkbox } from '../Checkbox';
import { SelectPlatformProps } from './SelectPlatform.interface';
import Tooltip from '../Tooltip/Tooltip';
import { useScreenWidth } from '../../hooks/useScreenWidth';
import { SelectedPlatformsContext } from './SelectedPlatformsContext';

interface Props {
  setTargetPlatform: (value: string[]) => void;
  value: string[];
  availablePlatforms: SelectPlatformProps[];
}

export function SelectedPlatform({ setTargetPlatform, value, availablePlatforms }: Props) {
  const { isMobile } = useScreenWidth();
  const platformIds = availablePlatforms.map(platform => platform.id);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { selectedPlatformsIds, setSelectedPlatformsIds } = useContext(SelectedPlatformsContext);

  const toggleDropdown = () => setIsDropdownOpen(prev => !prev);
  const isAllPlatformsSelected = selectedPlatformsIds.length === platformIds.length;
  const hasChannels = availablePlatforms.length > 0;

  const updateTargetPlatforms = (updatedSelectedIds: number[]) => {
    const selectedPlatformNames = availablePlatforms
      .filter(platform => updatedSelectedIds.includes(platform.id))
      .map(platform => platform.platform);

    setTargetPlatform(selectedPlatformNames);
  };

  const handlePlatformChange = (platformId: number) => {
    const isSelected = selectedPlatformsIds.includes(platformId);
    const updatedSelectedPlatforms = isSelected
      ? selectedPlatformsIds.filter(id => id !== platformId)
      : [...selectedPlatformsIds, platformId];

    setSelectedPlatformsIds(updatedSelectedPlatforms);
    updateTargetPlatforms(updatedSelectedPlatforms);
  };

  const handleSelectAll = () => {
    const updatedSelectedIds = isAllPlatformsSelected ? [] : [...platformIds];

    setSelectedPlatformsIds(updatedSelectedIds);
    updateTargetPlatforms(updatedSelectedIds);
  };

  const getButtonText = () => {
    if (isDropdownOpen) {
      return "Свернуть";
    }
    if (selectedPlatformsIds.length === 0) {
      return "Выберите площадку";
    }
    if (isAllPlatformsSelected) {
      return "Все площадки";
    }
    return "";
  };

  const renderButton = (
    <button
      onClick={toggleDropdown}
      className={styles["dropdown-checkbox__button"]}
      disabled={!hasChannels}
      type="button"
    >
      {getButtonText()}
      {!isDropdownOpen && !isAllPlatformsSelected && (
        <div className={styles["dropdown-checkbox__icons-wrapper"]}>
          {selectedPlatformsIds.map(platformId => {
            const platformData = availablePlatforms.find(platform => platform.id === platformId);
            return platformData && (
              <div className={styles["dropdown-checkbox__icon"]} key={platformData.id}>
                <PlatformIcon platform={platformData.icon} />
              </div>
            );
          })}
        </div>
      )}
      <span className={styles["dropdown-checkbox__indicator"]}>{isDropdownOpen ? '▼' : '▲'}</span>
    </button>
  );

  return (
    <div className={styles["dropdown-checkbox"]}>
      {(!hasChannels && !isMobile) ? (
        <Tooltip text="Нет включенных интегрированных каналов" position='top-center'>
          {renderButton}
        </Tooltip>
      ) : renderButton}

      {isDropdownOpen && (
        <div className={styles["dropdown-checkbox__content"]}>
          <div className={styles["dropdown-checkbox__item"]}>
            <div className={styles["dropdown-checkbox__all-platforms-wrapper"]}>
              <span className={styles["dropdown-checkbox__all-platforms"]}>Все площадки</span>
              <Checkbox
                value={isAllPlatformsSelected}
                onChange={handleSelectAll}
              />
            </div>
          </div>
          {availablePlatforms.map(platform => (
            <div className={styles["dropdown-checkbox__item"]} key={platform.id}>
              <div className={styles["dropdown-checkbox__platform-info"]}>
                <div className={styles["dropdown-checkbox__icon"]}>
                  <PlatformIcon platform={platform.icon} />
                </div>
                <span className={styles["dropdown-checkbox__platform-name"]}>{platform.name}</span>
              </div>
              <Checkbox
                value={selectedPlatformsIds.includes(platform.id)}
                onChange={() => handlePlatformChange(platform.id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
